// @ts-nocheck
/**
 * ContentsTable component.
 * Table displaying folder contents with sorting and selection.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Divider, Dropdown, Menu, Input, Table } from 'semantic-ui-react';
import { FormattedMessage, injectIntl } from 'react-intl';
import map from 'lodash/map';
import filter from 'lodash/filter';
import indexOf from 'lodash/indexOf';
import Popup from '@plone/volto/components/theme/Popup/Popup';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import ContentsIndexHeader from '@plone/volto/components/manage/Contents/ContentsIndexHeader';
import ContentsItem from '@plone/volto/components/manage/Contents/ContentsItem';
import Indexes from '@plone/volto/constants/Indexes';
import { contentsMessages as messages } from '../messages/contentsMessages';

import {
  checkboxUncheckedSVG,
  checkboxCheckedSVG,
  checkboxIndeterminateSVG,
  configurationSVG,
  sortDownSVG,
  sortUpSVG,
  downKeySVG,
  zoomSVG,
  deleteSVG,
} from '../icons';

/**
 * ContentsTable component
 */
const ContentsTable = ({
  items,
  selected,
  selectedCount,
  index,
  filteredItems,
  selectedMenuFilter,
  onSelectAll,
  onSelectNone,
  onSelect,
  onDeselect,
  onChangeSelected,
  onSortItems,
  onOrderIndex,
  onCut,
  onCopy,
  onDelete,
  onOrderItem,
  onMoveToTop,
  onMoveToBottom,
  getFieldById,
  intl,
}) => {
  return (
    <Table selectable compact singleLine attached>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>
            <Popup
              menu={true}
              position="bottom left"
              flowing={true}
              basic={true}
              on="click"
              popper={{
                className: 'dropdown-popup',
              }}
              trigger={
                <Icon
                  name={configurationSVG}
                  size="24px"
                  color="#826a6a"
                  className="dropdown-popup-trigger configuration-svg"
                />
              }
            >
              <Menu vertical borderless fluid>
                <Menu.Header
                  content={intl.formatMessage(messages.rearrangeBy)}
                />
                {map(
                  [
                    'id',
                    'sortable_title',
                    'EffectiveDate',
                    'CreationDate',
                    'ModificationDate',
                    'portal_type',
                  ],
                  (idx) => (
                    <Dropdown
                      key={idx}
                      item
                      simple
                      className={`sort_${idx} icon-align`}
                      icon={
                        <Icon name={downKeySVG} size="24px" className="left" />
                      }
                      text={intl.formatMessage({
                        id: Indexes[idx].label,
                      })}
                    >
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={onSortItems}
                          value={`${Indexes[idx].sort_on}|ascending`}
                          className={`sort_${Indexes[idx].sort_on}_ascending icon-align`}
                        >
                          <Icon name={sortDownSVG} size="24px" />{' '}
                          <FormattedMessage
                            id="Ascending"
                            defaultMessage="Ascending"
                          />
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={onSortItems}
                          value={`${Indexes[idx].sort_on}|descending`}
                          className={`sort_${Indexes[idx].sort_on}_descending icon-align`}
                        >
                          <Icon name={sortUpSVG} size="24px" />{' '}
                          <FormattedMessage
                            id="Descending"
                            defaultMessage="Descending"
                          />
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ),
                )}
              </Menu>
            </Popup>
          </Table.HeaderCell>
          <Table.HeaderCell>
            <Popup
              menu={true}
              position="bottom left"
              flowing={true}
              basic={true}
              on="click"
              popper={{
                className: 'dropdown-popup',
              }}
              trigger={
                <Icon
                  name={
                    selected.length === 0
                      ? checkboxUncheckedSVG
                      : selected.length === items.length
                        ? checkboxCheckedSVG
                        : checkboxIndeterminateSVG
                  }
                  color={selected.length > 0 ? '#007eb1' : '#826a6a'}
                  className="dropdown-popup-trigger"
                  size="24px"
                />
              }
            >
              <Menu vertical borderless fluid>
                <Menu.Header content={intl.formatMessage(messages.select)} />
                <Menu.Item onClick={onSelectAll}>
                  <Icon name={checkboxCheckedSVG} color="#007eb1" size="24px" />{' '}
                  <FormattedMessage id="All" defaultMessage="All" />
                </Menu.Item>
                <Menu.Item onClick={onSelectNone}>
                  <Icon name={checkboxUncheckedSVG} size="24px" />{' '}
                  <FormattedMessage id="None" defaultMessage="None" />
                </Menu.Item>
                <Divider />
                <Menu.Header
                  content={intl.formatMessage(messages.selected, {
                    count: selected.length,
                  })}
                />
                <Input
                  icon={<Icon name={zoomSVG} size="24px" />}
                  iconPosition="left"
                  className="item search"
                  placeholder={intl.formatMessage(messages.filter)}
                  value={selectedMenuFilter || ''}
                  onChange={onChangeSelected}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
                <Menu.Menu scrolling>
                  {map(filteredItems, (item) => (
                    <Menu.Item key={item} value={item} onClick={onDeselect}>
                      <Icon name={deleteSVG} color="#e40166" size="24px" />{' '}
                      {getFieldById(item, 'title')}
                    </Menu.Item>
                  ))}
                </Menu.Menu>
              </Menu>
            </Popup>
          </Table.HeaderCell>
          <Table.HeaderCell width={Math.ceil(16 / selectedCount)}>
            <FormattedMessage id="Title" defaultMessage="Title" />
          </Table.HeaderCell>
          {map(
            index.order,
            (idx, order) =>
              index.values[idx].selected && (
                <ContentsIndexHeader
                  key={idx}
                  width={Math.ceil(16 / selectedCount)}
                  label={index.values[idx].label}
                  order={order}
                  onOrderIndex={onOrderIndex}
                />
              ),
          )}
          <Table.HeaderCell textAlign="right">
            <FormattedMessage id="Actions" defaultMessage="Actions" />
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {items.map((item, order) => (
          <ContentsItem
            key={item['@id']}
            item={item}
            order={order}
            selected={indexOf(selected, item['@id']) !== -1}
            onClick={onSelect}
            indexes={filter(
              map(index.order, (idx) => ({
                id: idx,
                type: index.values[idx].type,
              })),
              (idx) => index.values[idx.id].selected,
            )}
            onCut={onCut}
            onCopy={onCopy}
            onDelete={onDelete}
            onOrderItem={onOrderItem}
            onMoveToTop={onMoveToTop}
            onMoveToBottom={onMoveToBottom}
          />
        ))}
      </Table.Body>
    </Table>
  );
};

ContentsTable.propTypes = {
  items: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  selectedCount: PropTypes.number.isRequired,
  index: PropTypes.object.isRequired,
  filteredItems: PropTypes.array.isRequired,
  selectedMenuFilter: PropTypes.string,
  onSelectAll: PropTypes.func.isRequired,
  onSelectNone: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDeselect: PropTypes.func.isRequired,
  onChangeSelected: PropTypes.func.isRequired,
  onSortItems: PropTypes.func.isRequired,
  onOrderIndex: PropTypes.func.isRequired,
  onCut: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onOrderItem: PropTypes.func.isRequired,
  onMoveToTop: PropTypes.func.isRequired,
  onMoveToBottom: PropTypes.func.isRequired,
  getFieldById: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

ContentsTable.defaultProps = {
  selectedMenuFilter: '',
};

export default injectIntl(ContentsTable);
